import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import RTPageLayout from '../common/RTPageLayout';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import { apiGet, apiPost } from '../../api/apiClient';
import { toastError, toastConfirm } from '../common/toast';
import TemplateSendJobModal from './TemplateSendJobModal';
import '../common/rtCommon.css';
import './WhatsAppTemplateManagement.css';

const SendWhatsAppTemplateMessage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [preview, setPreview] = useState(null);
  const [showJobModal, setShowJobModal] = useState(false);
  const [jobSummary, setJobSummary] = useState(null);

  const loadTemplates = useCallback(async () => {
    try {
      setLoadingTemplates(true);
      const response = await apiGet('/api/whatsapp/send-template/templates');
      const data = response.data || response;
      const list = Array.isArray(data.templates) ? data.templates : [];
      setTemplates(list);
    } catch (err) {
      console.error(err);
      toastError(
        err?.response?.data?.detail || 'Failed to load sendable templates',
      );
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    if (!templateName) {
      setPreview(null);
      return;
    }

    let cancelled = false;
    const loadPreview = async () => {
      try {
        setLoadingPreview(true);
        const response = await apiGet('/api/whatsapp/send-template/preview', {
          params: { template_name: templateName },
        });
        if (cancelled) return;
        setPreview(response.data || response);
      } catch (err) {
        if (cancelled) return;
        console.error(err);
        setPreview(null);
        toastError(
          err?.response?.data?.detail || 'Failed to preview template message',
        );
      } finally {
        if (!cancelled) setLoadingPreview(false);
      }
    };

    loadPreview();
    return () => {
      cancelled = true;
    };
  }, [templateName]);

  const totalActive = preview?.total_active_allocations ?? 0;
  const canSend = Boolean(preview?.can_send) && !sending && !loadingPreview;

  const selectedLabel = useMemo(() => {
    const t = templates.find((x) => x.name === templateName);
    return t?.name || templateName;
  }, [templates, templateName]);

  const sampleHeading = useMemo(() => {
    const pg = preview?.sample_pg;
    if (!pg?.name) {
      return 'Sample Message';
    }
    const building = pg.building_name || 'Unknown Building';
    const roomLabel = pg.room_name || (pg.room_number ? `Room ${pg.room_number}` : null);
    if (roomLabel) {
      return `Sample Message as to be received by ${pg.name} in ${building} (${roomLabel})`;
    }
    return `Sample Message as to be received by ${pg.name} in ${building}`;
  }, [preview]);

  const handleSend = async () => {
    if (!templateName || !preview) return;

    const confirmed = await toastConfirm(
      true,
      `Send template "${selectedLabel}" to all ${totalActive} active allocation${
        totalActive === 1 ? '' : 's'
      }? This will queue a background job.`,
    );
    if (!confirmed) return;

    try {
      setSending(true);
      setJobSummary({
        job_id: null,
        template_name: selectedLabel,
        total_allocations: totalActive,
        status: 'starting',
        message: 'Starting background processing...',
      });
      setShowJobModal(true);

      const response = await apiPost('/api/whatsapp/send-template', {
        template_name: templateName,
      });
      const data = response.data || response;

      setJobSummary({
        job_id: data.job_id,
        template_name: data.template_name || selectedLabel,
        total_allocations: data.total_allocations ?? totalActive,
        status: 'processing',
        message:
          data.message ||
          'Processing in background. This may take several minutes. You can close this modal and continue working.',
      });
    } catch (err) {
      console.error(err);
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setJobSummary({
          job_id: null,
          template_name: selectedLabel,
          total_allocations: totalActive,
          status: 'timeout',
          message:
            'Request timed out. The background job may still be running. Please check Background Jobs to verify.',
        });
        setShowJobModal(true);
      } else {
        setShowJobModal(false);
        setJobSummary(null);
        toastError(
          err?.response?.data?.detail || 'Failed to start template send job',
        );
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <RTPageLayout
      activePath={location.pathname}
      title="Send Template Message"
      mainSection={true}
      actions={
        <RTButton
          variant="secondary"
          onClick={() => navigate('/settings/whatsapp-templates')}
        >
          &#8592; Back to Templates
        </RTButton>
      }
    >
      <div className="wa-tpl-page wa-send-tpl wa-send-tpl-centered">
        <p className="wa-tpl-hint wa-send-tpl-intro">
          Choose an approved template that is marked eligible for bulk send.
          Tags are filled from mapped table columns for each active allocation.
          Messages might take some time check the status of messages in
          Reports {'->'} Background jobs.
        </p>

        {loadingTemplates ? (
          <div className="wa-tpl-spinner">
            <RTSpinner />
          </div>
        ) : (
          <>
            <div className="wa-send-tpl-field">
              <label htmlFor="wa-send-template-name">Template name</label>
              <select
                id="wa-send-template-name"
                className="wa-send-tpl-select"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
              >
                <option value="">Select a template…</option>
                {templates.map((t) => (
                  <option
                    key={t.name}
                    value={t.name}
                    disabled={t.can_send === false}
                  >
                    {t.name}
                    {t.can_send === false ? ' (unmapped tags)' : ''}
                  </option>
                ))}
              </select>
              {templates.length === 0 ? (
                <div className="wa-tpl-warning">
                  No templates are eligible for bulk send. Create a template
                  with Eligible for bulk send set to Yes, or enable Bulk Send
                  on an existing template.
                </div>
              ) : null}
            </div>

            {templateName ? (
              loadingPreview ? (
                <div className="wa-tpl-spinner">
                  <RTSpinner message="Loading sample message…" />
                </div>
              ) : preview ? (
                <div className="wa-send-tpl-preview-block">
                  <div className="wa-send-tpl-audience">
                    Message will be sent to all {totalActive} active allocation
                    {totalActive === 1 ? '' : 's'}.
                  </div>

                  {preview.unmapped_tags?.length ? (
                    <div className="wa-tpl-warning">
                      Unmapped tags (cannot send):{' '}
                      {preview.unmapped_tags.join(', ')}
                    </div>
                  ) : null}

                  {totalActive === 0 ? (
                    <div className="wa-tpl-warning">
                      There are no active allocations to send to.
                    </div>
                  ) : null}

                  <div className="wa-send-tpl-message-label">{sampleHeading}</div>
                  <pre className="wa-send-tpl-message">
                    {preview.sample_message || '(empty)'}
                  </pre>

                  {preview.tag_mappings?.length ? (
                    <div className="wa-send-tpl-mappings">
                      <div className="wa-send-tpl-message-label">
                        Tag mappings for this template
                      </div>
                      <ul>
                        {preview.tag_mappings.map((m) => (
                          <li key={m.tag_key}>
                            <code>{`{{${m.tag_key}}}`}</code>
                            {' → '}
                            <code>
                              {m.source_table}.{m.source_column}
                            </code>
                            {m.sample_value != null ? (
                              <>
                                {' '}
                                (sample: <strong>{m.sample_value}</strong>)
                              </>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  <div className="wa-send-tpl-actions wa-send-tpl-actions-right">
                    <RTButton
                      variant="primary"
                      onClick={handleSend}
                      disabled={!canSend}
                    >
                      {sending ? 'Starting…' : 'Send'}
                    </RTButton>
                  </div>
                </div>
              ) : null
            ) : null}
          </>
        )}
      </div>

      <TemplateSendJobModal
        isOpen={showJobModal}
        onClose={() => setShowJobModal(false)}
        summaryData={jobSummary}
      />
    </RTPageLayout>
  );
};

export default SendWhatsAppTemplateMessage;
